<script setup>
import { ref, computed, onBeforeUnmount } from 'vue';
import { useRoute } from 'vue-router';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import {
  fetchMarketplaceJson,
  postMarketplaceJsonBody,
  postMarketplaceBlob,
} from 'dashboard/helpers/marketplaceApi';

const route = useRoute();
const accountId = () => route.params.accountId;

// ---------------------------------------------------------------------------
// DURUM — mode: 'realtime' (OpenAI Realtime/WebRTC, asıl deneyim) | 'classic'
// (eski STT→LLM→TTS zinciri, otomatik YEDEK). status her iki modda ortak.
// Erişilebilirlik ilkesi: her durumun SESLİ karşılığı var (earcon + gerektiğinde
// sözlü duyuru); ekran yalnızca ikincil destek.
// ---------------------------------------------------------------------------
const mode = ref('realtime');
const status = ref('idle'); // idle|connecting|listening|thinking|speaking|error
const connected = ref(false);
const question = ref('');
const answer = ref('');
const liveAnswer = ref(''); // realtime akan transkript
const errorMsg = ref('');
const infoMsg = ref('');
const textInput = ref('');
const history = ref([]); // klasik mod bağlamı

const STATUS_TEXT = {
  idle: 'Hazır. Başlamak için büyük butona dokunun.',
  connecting: 'Bağlanıyorum…',
  listening: 'Dinliyorum, konuşabilirsiniz.',
  thinking: 'Düşünüyorum, verilere bakıyorum…',
  speaking: 'Cevap veriyorum. Sözümü kesebilirsiniz.',
  error: 'Bir sorun oldu.',
};
const statusText = computed(() => STATUS_TEXT[status.value] || '');

// ---------------------------------------------------------------------------
// SES ALTYAPISI + EARCON'LAR (durumların sesli işaretleri — dosyasız, WebAudio)
// ---------------------------------------------------------------------------
let audioCtx = null;
let thinkTimer = null;

const ensureAudio = () => {
  if (!audioCtx) {
    const Ctx = window.AudioContext || window.webkitAudioContext;
    if (Ctx) audioCtx = new Ctx();
  }
  if (audioCtx && audioCtx.state === 'suspended') audioCtx.resume();
};

const playTone = (freq, dur = 0.12, delay = 0, gainV = 0.14) => {
  if (!audioCtx) return;
  const t = audioCtx.currentTime + delay;
  const osc = audioCtx.createOscillator();
  const gain = audioCtx.createGain();
  osc.type = 'sine';
  osc.frequency.value = freq;
  gain.gain.setValueAtTime(gainV, t);
  gain.gain.exponentialRampToValueAtTime(0.001, t + dur);
  osc.connect(gain);
  gain.connect(audioCtx.destination);
  osc.start(t);
  osc.stop(t + dur + 0.02);
};

const stopThinkTick = () => {
  if (thinkTimer) {
    clearInterval(thinkTimer);
    thinkTimer = null;
  }
};

const earcon = name => {
  ensureAudio();
  if (!audioCtx) return;
  if (name === 'connect') {
    playTone(523, 0.1);
    playTone(659, 0.12, 0.12);
  } else if (name === 'ready') {
    playTone(880, 0.09);
  } else if (name === 'listen') {
    playTone(660, 0.07, 0, 0.1);
  } else if (name === 'think') {
    stopThinkTick();
    playTone(440, 0.06, 0, 0.07);
    thinkTimer = setInterval(() => playTone(440, 0.06, 0, 0.07), 1600);
  } else if (name === 'error') {
    stopThinkTick();
    playTone(392, 0.14);
    playTone(262, 0.2, 0.16);
  } else if (name === 'disconnect') {
    stopThinkTick();
    playTone(494, 0.1);
    playTone(392, 0.1, 0.12);
    playTone(262, 0.16, 0.24);
  }
};

// ---------------------------------------------------------------------------
// GERÇEK ZAMANLI MOTOR (OpenAI Realtime, WebRTC). Anahtar frontend'e inmez:
// backend realtime_session kısa ömürlü token verir; araç çağrıları
// assistant_tool endpoint'i üzerinden auth'lu backend'de çalışır.
// ---------------------------------------------------------------------------
let pc = null;
let dc = null;
let micStream = null;
let remoteAudio = null;
let reconnectTried = false;
let manualClose = false;

const dcSend = obj => {
  if (dc && dc.readyState === 'open') dc.send(JSON.stringify(obj));
};

const cleanupRealtime = () => {
  stopThinkTick();
  try {
    if (dc) dc.close();
  } catch {
    /* kapalı olabilir */
  }
  try {
    if (pc) pc.close();
  } catch {
    /* kapalı olabilir */
  }
  if (micStream) micStream.getTracks().forEach(t => t.stop());
  if (remoteAudio) remoteAudio.srcObject = null;
  dc = null;
  pc = null;
  micStream = null;
  connected.value = false;
};

const fallbackToClassic = reason => {
  cleanupRealtime();
  mode.value = 'classic';
  status.value = 'idle';
  const msg = `${reason} Klasik sesli moda geçtim; butona basıp konuşabilirsiniz.`;
  infoMsg.value = msg;
  earcon('error');
  // Klasik motor asagida tanimli; calisma zamaninda hazir olur.
  // eslint-disable-next-line no-use-before-define
  speakClassic(msg); // sessiz bırakma: geçişi SESLE duyur
};

// derin_analiz sonucunu bekle (Sidekiq → Redis → poll).
const pollDeepResult = async analizId => {
  for (let i = 0; i < 50; i += 1) {
    // eslint-disable-next-line no-await-in-loop
    await new Promise(r => {
      setTimeout(r, 3000);
    });
    try {
      // eslint-disable-next-line no-await-in-loop
      const data = await fetchMarketplaceJson(
        accountId(),
        `meta_ads/deep_result?analiz_id=${analizId}`
      );
      if (data.durum === 'tamam' || data.durum === 'hata') return data;
    } catch {
      /* geçici ağ hatası: denemeye devam */
    }
  }
  return { durum: 'hata', mesaj: 'Derin analiz zaman aşımına uğradı.' };
};

const sendToolOutput = (callId, obj) => {
  dcSend({
    type: 'conversation.item.create',
    item: {
      type: 'function_call_output',
      call_id: callId,
      output: JSON.stringify(obj),
    },
  });
};

const runTool = async item => {
  try {
    const data = await postMarketplaceJsonBody(
      accountId(),
      'meta_ads/assistant_tool',
      { name: item.name, args: JSON.parse(item.arguments || '{}') }
    );
    return data.result;
  } catch {
    return { hata: 'araç şu an çalıştırılamadı, kullanıcıya söyle' };
  }
};

// Derin analiz: "başladı" cevabı verildikten sonra sonuç hazır olunca modele
// ayrı bir sistem mesajı olarak iletilir, model sesli özetler.
const followUpDeepAnalysis = async result => {
  earcon('think');
  const final = await pollDeepResult(result.analiz_id);
  stopThinkTick();
  const text =
    final.durum === 'tamam'
      ? `SİSTEM: Derin analiz sonucu hazır. Kullanıcıya ana bulguları sesli ve akıcı biçimde aktar:\n${final.metin}`
      : `SİSTEM: Derin analiz başarısız oldu (${final.mesaj}). Kullanıcıya kibarca söyle ve daha basit bir soruyla yardımcı olmayı öner.`;
  dcSend({
    type: 'conversation.item.create',
    item: {
      type: 'message',
      role: 'user',
      content: [{ type: 'input_text', text }],
    },
  });
  dcSend({ type: 'response.create' });
};

// KRİTİK: Model birden çok aracı PARALEL çağırabilir (ör. 3 reklam hesabı).
// TÜM sonuçlar toplanmadan response.create GÖNDERİLMEZ — yoksa model ilk
// sonuçla konuşmaya başlayıp kalan hesaplar için "veri çekemedim" diyor ve
// sonraki response.create'ler "aktif cevap var" hatasına düşüyordu.
const handleFunctionCalls = async items => {
  status.value = 'thinking';
  earcon('think');
  const results = await Promise.all(
    items.map(async item => ({ item, out: await runTool(item) }))
  );
  results.forEach(({ item, out }) => sendToolOutput(item.call_id, out));
  dcSend({ type: 'response.create' }); // hepsi hazır → TEK cevap
  results
    .filter(
      ({ item, out }) => item.name === 'derin_analiz' && out && out.analiz_id
    )
    .forEach(({ out }) => followUpDeepAnalysis(out));
};

const handleServerEvent = ev => {
  switch (ev.type) {
    case 'input_audio_buffer.speech_started':
      stopThinkTick();
      status.value = 'listening';
      break;
    case 'conversation.item.input_audio_transcription.completed':
      if (ev.transcript) question.value = ev.transcript.trim();
      break;
    case 'response.created':
      status.value = 'thinking';
      earcon('think');
      liveAnswer.value = '';
      break;
    case 'response.output_audio_transcript.delta':
      stopThinkTick();
      status.value = 'speaking';
      liveAnswer.value += ev.delta || '';
      break;
    case 'response.output_audio_transcript.done':
      if (ev.transcript) answer.value = ev.transcript;
      break;
    case 'output_audio_buffer.started':
      stopThinkTick();
      status.value = 'speaking';
      break;
    case 'output_audio_buffer.stopped':
    case 'output_audio_buffer.cleared':
      if (status.value === 'speaking') status.value = 'listening';
      break;
    case 'response.done': {
      stopThinkTick();
      const calls = ((ev.response && ev.response.output) || []).filter(
        i => i.type === 'function_call'
      );
      if (calls.length) {
        handleFunctionCalls(calls);
      } else if (status.value === 'thinking') {
        status.value = 'listening';
      }
      break;
    }
    case 'error':
      // Oturum içi tekil hatalar ölümcül değil; sesli işaret ver, devam et.
      earcon('error');
      break;
    default:
      break;
  }
};

const connectRealtime = async () => {
  ensureAudio();
  manualClose = false;
  errorMsg.value = '';
  infoMsg.value = '';
  status.value = 'connecting';
  earcon('connect');

  let token;
  try {
    token = await postMarketplaceJsonBody(
      accountId(),
      'meta_ads/realtime_session',
      {}
    );
  } catch {
    fallbackToClassic('Hızlı ses bağlantısı şu an kurulamadı.');
    return;
  }

  try {
    micStream = await navigator.mediaDevices.getUserMedia({ audio: true });
  } catch {
    status.value = 'error';
    errorMsg.value =
      'Mikrofon izni alınamadı. Tarayıcı ayarlarından mikrofona izin verin.';
    earcon('error');
    return;
  }

  try {
    pc = new RTCPeerConnection();
    remoteAudio = remoteAudio || new Audio();
    remoteAudio.autoplay = true;
    pc.ontrack = e => {
      remoteAudio.srcObject = e.streams[0];
    };
    micStream.getTracks().forEach(t => pc.addTrack(t, micStream));

    dc = pc.createDataChannel('oai-events');
    dc.onmessage = e => {
      try {
        handleServerEvent(JSON.parse(e.data));
      } catch {
        /* bozuk olay: yok say */
      }
    };
    dc.onopen = () => {
      connected.value = true;
      reconnectTried = false;
      status.value = 'listening';
      earcon('ready');
      infoMsg.value = 'Bağlandım. Konuşabilirsiniz — sözümü kesebilirsiniz.';
    };

    pc.onconnectionstatechange = () => {
      if (!pc || manualClose) return;
      if (['failed', 'disconnected', 'closed'].includes(pc.connectionState)) {
        if (!connected.value) return;
        earcon('disconnect');
        cleanupRealtime();
        if (!reconnectTried) {
          reconnectTried = true;
          infoMsg.value = 'Bağlantı koptu, yeniden bağlanıyorum…';
          setTimeout(() => connectRealtime(), 1500);
        } else {
          fallbackToClassic('Ses bağlantısı tekrar koptu.');
        }
      }
    };

    const offer = await pc.createOffer();
    await pc.setLocalDescription(offer);
    const resp = await fetch(
      `https://api.openai.com/v1/realtime/calls?model=${encodeURIComponent(token.model)}`,
      {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token.value}`,
          'Content-Type': 'application/sdp',
        },
        body: offer.sdp,
      }
    );
    if (!resp.ok) throw new Error(`calls ${resp.status}`);
    const answerSdp = await resp.text();
    await pc.setRemoteDescription({ type: 'answer', sdp: answerSdp });
  } catch {
    fallbackToClassic('Gerçek zamanlı ses kurulamadı.');
  }
};

const stopSpeakingRealtime = () => {
  dcSend({ type: 'response.cancel' });
  dcSend({ type: 'output_audio_buffer.clear' });
  status.value = 'listening';
};

const disconnectRealtime = () => {
  manualClose = true;
  earcon('disconnect');
  cleanupRealtime();
  status.value = 'idle';
  infoMsg.value = 'Oturum kapatıldı. Tekrar başlamak için butona dokunun.';
};

const sendTextRealtime = text => {
  question.value = text;
  dcSend({
    type: 'conversation.item.create',
    item: {
      type: 'message',
      role: 'user',
      content: [{ type: 'input_text', text }],
    },
  });
  dcSend({ type: 'response.create' });
};

// ---------------------------------------------------------------------------
// KLASİK MOD (yedek): tarayıcı STT → assistant → OpenAI TTS.
// DÜZELTME: cevap artık CÜMLE CÜMLE bölünüp sırayla okunur — eskiden tek
// çağrıda 1200 karakterde sessizce kesiliyordu (erişilebilirlik bug'ı).
// ---------------------------------------------------------------------------
let currentSource = null;
let speechSeq = 0;

const splitChunks = (text, max = 300) => {
  const sentences = text.match(/[^.!?…\n]+[.!?…]*\s*/g) || [text];
  const chunks = [];
  let cur = '';
  sentences.forEach(s => {
    if ((cur + s).length > max && cur) {
      chunks.push(cur);
      cur = s;
    } else {
      cur += s;
    }
  });
  if (cur.trim()) chunks.push(cur);
  return chunks.map(c => c.trim()).filter(Boolean);
};

const stopSpeakClassic = () => {
  speechSeq += 1; // sıradaki parçaları iptal et
  if (currentSource) {
    try {
      currentSource.onended = null;
      currentSource.stop();
    } catch {
      /* zaten durmuş */
    }
    currentSource = null;
  }
  if (status.value === 'speaking') status.value = 'idle';
};

const playChunk = (buf, seq) =>
  new Promise(resolve => {
    if (seq !== speechSeq || !audioCtx) {
      resolve();
      return;
    }
    audioCtx.decodeAudioData(buf).then(
      audioBuffer => {
        if (seq !== speechSeq) {
          resolve();
          return;
        }
        const source = audioCtx.createBufferSource();
        source.buffer = audioBuffer;
        const compressor = audioCtx.createDynamicsCompressor();
        const gain = audioCtx.createGain();
        gain.gain.value = 2.6;
        source.connect(compressor);
        compressor.connect(gain);
        gain.connect(audioCtx.destination);
        source.onended = () => {
          if (currentSource === source) currentSource = null;
          resolve();
        };
        currentSource = source;
        source.start(0);
      },
      () => resolve()
    );
  });

const speakClassic = async text => {
  if (!text) return;
  stopSpeakClassic();
  ensureAudio();
  if (!audioCtx) return;
  speechSeq += 1;
  const seq = speechSeq;
  status.value = 'speaking';
  const chunks = splitChunks(text);
  for (let i = 0; i < chunks.length; i += 1) {
    if (seq !== speechSeq) return;
    try {
      // eslint-disable-next-line no-await-in-loop
      const blob = await postMarketplaceBlob(
        accountId(),
        'meta_ads/report_speech',
        {
          text: chunks[i],
        }
      );
      // eslint-disable-next-line no-await-in-loop
      const buf = await blob.arrayBuffer();
      // eslint-disable-next-line no-await-in-loop
      await playChunk(buf, seq);
    } catch {
      /* parça okunamadı: sonrakine geç, metin ekranda */
    }
  }
  if (seq === speechSeq && status.value === 'speaking') status.value = 'idle';
};

const askClassic = async q => {
  const text = (q || '').trim();
  if (!text) return;
  question.value = text;
  answer.value = '';
  errorMsg.value = '';
  status.value = 'thinking';
  earcon('think');
  stopSpeakClassic();
  try {
    const data = await postMarketplaceJsonBody(
      accountId(),
      'meta_ads/assistant',
      {
        question: text,
        history: history.value.slice(-6),
      }
    );
    stopThinkTick();
    answer.value = data.answer || 'Cevap alınamadı.';
    history.value.push(
      { role: 'user', content: text },
      { role: 'assistant', content: answer.value }
    );
    if (history.value.length > 12) history.value = history.value.slice(-12);
    speakClassic(answer.value);
  } catch {
    stopThinkTick();
    status.value = 'idle';
    errorMsg.value = 'Cevap alınamadı, tekrar deneyin.';
    earcon('error');
    speakClassic('Üzgünüm, cevabı alamadım. Lütfen tekrar deneyin.');
  }
};

const SR =
  typeof window !== 'undefined' &&
  (window.SpeechRecognition || window.webkitSpeechRecognition);
let recognition = null;
let finalHandled = false;

const startListeningClassic = () => {
  ensureAudio();
  if (!SR) {
    errorMsg.value =
      'Bu tarayıcı sesli komutu desteklemiyor. Aşağıdan yazarak sorabilirsiniz.';
    return;
  }
  stopSpeakClassic();
  errorMsg.value = '';
  question.value = '';
  answer.value = '';
  finalHandled = false;
  recognition = new SR();
  recognition.lang = 'tr-TR';
  recognition.interimResults = true;
  recognition.maxAlternatives = 1;
  recognition.continuous = false;
  recognition.onresult = e => {
    const transcript = Array.from(e.results)
      .map(r => r[0].transcript)
      .join('')
      .trim();
    question.value = transcript;
    const last = e.results[e.results.length - 1];
    if (last.isFinal && !finalHandled && transcript) {
      finalHandled = true;
      try {
        recognition.stop();
      } catch {
        /* yok say */
      }
      askClassic(transcript);
    }
  };
  recognition.onerror = evt => {
    if (status.value === 'listening') status.value = 'idle';
    if (evt.error !== 'aborted' && evt.error !== 'no-speech') {
      errorMsg.value = 'Sesi anlayamadım, tekrar dener misiniz?';
      earcon('error');
    }
  };
  recognition.onend = () => {
    if (status.value === 'listening' && !finalHandled) status.value = 'idle';
  };
  try {
    recognition.start();
    status.value = 'listening';
    earcon('listen');
    if (navigator.vibrate) navigator.vibrate(40);
  } catch {
    status.value = 'idle';
  }
};

// ---------------------------------------------------------------------------
// ANA BUTON + ORTAK EYLEMLER
// ---------------------------------------------------------------------------
const onMainButton = () => {
  ensureAudio();
  if (mode.value === 'realtime') {
    if (!connected.value && status.value !== 'connecting') {
      connectRealtime();
    } else if (status.value === 'speaking') {
      stopSpeakingRealtime();
    } else {
      earcon('listen'); // bağlıyım ve dinliyorum işareti
    }
    return;
  }
  if (status.value === 'listening') {
    try {
      if (recognition) recognition.stop();
    } catch {
      /* yok say */
    }
    status.value = 'idle';
  } else if (status.value === 'speaking') {
    stopSpeakClassic();
  } else if (status.value === 'idle' || status.value === 'error') {
    startListeningClassic();
  }
};

const submitText = () => {
  ensureAudio();
  const t = textInput.value.trim();
  if (!t) return;
  textInput.value = '';
  if (mode.value === 'realtime' && connected.value) sendTextRealtime(t);
  else askClassic(t);
};

const askExample = ex => {
  ensureAudio();
  if (mode.value === 'realtime' && connected.value) sendTextRealtime(ex);
  else askClassic(ex);
};

const replayClassic = () => {
  ensureAudio();
  if (status.value === 'speaking') stopSpeakClassic();
  else if (answer.value) speakClassic(answer.value);
};

const retryRealtime = () => {
  mode.value = 'realtime';
  reconnectTried = false;
  connectRealtime();
};

const buttonLabel = computed(() => {
  if (mode.value === 'realtime') {
    if (status.value === 'connecting') return 'Bağlanıyorum…';
    if (!connected.value) return 'Konuşmayı Başlat';
    if (status.value === 'speaking')
      return 'Konuşuyorum… (susturmak için dokun)';
    if (status.value === 'thinking') return 'Düşünüyorum…';
    return 'Dinliyorum… konuşun';
  }
  if (status.value === 'listening') return 'Dinliyorum… (durdurmak için bas)';
  if (status.value === 'thinking') return 'Düşünüyorum…';
  if (status.value === 'speaking') return 'Konuşuyorum… (durdurmak için bas)';
  return 'Sormak için bas';
});

const displayedAnswer = computed(() =>
  mode.value === 'realtime' && liveAnswer.value
    ? liveAnswer.value
    : answer.value
);

onBeforeUnmount(() => {
  manualClose = true;
  cleanupRealtime();
  stopSpeakClassic();
  stopThinkTick();
  if (recognition) {
    try {
      recognition.abort();
    } catch {
      /* yok say */
    }
  }
});
</script>

<template>
  <div
    class="flex flex-col items-center w-full h-full overflow-hidden bg-n-background"
  >
    <header
      class="flex-shrink-0 w-full px-5 py-4 text-center border-b sm:px-7 border-n-weak"
    >
      <h1 class="text-2xl font-bold text-n-slate-12">Sesli Asistan</h1>
      <p class="text-lg text-n-slate-11">
        {{
          mode === 'realtime'
            ? 'Gerçek zamanlı mod: bağlan, doğal konuş, sözünü kesebilirsin'
            : 'Klasik mod (yedek): butona bas, sorunu sor'
        }}
      </p>
    </header>

    <div
      class="flex flex-col items-center flex-1 w-full max-w-2xl px-5 py-6 overflow-y-auto"
    >
      <!-- Ekran okuyucu + görme destekli CANLI DURUM (her değişiklik okunur) -->
      <p
        aria-live="assertive"
        role="status"
        class="mb-4 text-xl font-semibold text-center text-n-slate-11"
      >
        {{ statusText }}
      </p>

      <!-- Büyük ana buton: tek dokunuş her şeyi yönetir -->
      <button
        type="button"
        :aria-label="buttonLabel"
        class="relative flex flex-col items-center justify-center transition-colors rounded-full size-52 sm:size-60 flex-shrink-0 outline-none focus:ring-4 focus:ring-n-brand/40"
        :class="
          status === 'listening'
            ? 'bg-n-ruby-9 text-white'
            : status === 'thinking' || status === 'connecting'
              ? 'bg-n-amber-9 text-white'
              : status === 'speaking'
                ? 'bg-n-teal-9 text-white'
                : 'bg-n-brand text-white hover:opacity-90'
        "
        @click="onMainButton"
      >
        <span
          v-if="status === 'listening'"
          class="absolute inset-0 rounded-full bg-n-ruby-9/40 animate-ping"
        />
        <Spinner
          v-if="status === 'thinking' || status === 'connecting'"
          class="size-16"
        />
        <span
          v-else
          class="size-24"
          :class="status === 'speaking' ? 'i-lucide-volume-2' : 'i-lucide-mic'"
        />
        <span class="relative mt-2 text-lg font-bold text-center px-4">
          {{ buttonLabel }}
        </span>
      </button>

      <!-- Oturum kapat (realtime bağlıyken) -->
      <button
        v-if="mode === 'realtime' && connected"
        type="button"
        class="px-6 py-3 mt-4 text-lg font-semibold text-white rounded-xl bg-n-ruby-9"
        @click="disconnectRealtime"
      >
        Oturumu Kapat
      </button>

      <!-- Klasik moddaysa gerçek zamanlıya dönüş -->
      <button
        v-if="mode === 'classic'"
        type="button"
        class="px-6 py-3 mt-4 text-lg font-semibold text-white rounded-xl bg-n-brand"
        @click="retryRealtime"
      >
        Gerçek Zamanlı Moda Geç
      </button>

      <!-- Bilgi / hata (yüksek kontrast, büyük) -->
      <p
        v-if="infoMsg"
        aria-live="polite"
        class="mt-5 text-lg text-center text-n-slate-11"
      >
        {{ infoMsg }}
      </p>
      <p
        v-if="errorMsg"
        aria-live="assertive"
        class="mt-5 text-xl text-center text-n-ruby-11"
      >
        {{ errorMsg }}
      </p>

      <!-- Soru (son anlaşılan) -->
      <p
        v-if="question"
        class="mt-6 text-2xl font-semibold text-center text-n-slate-12"
      >
        “{{ question }}”
      </p>

      <!-- Cevap (ikincil görsel destek: çok büyük yazı) -->
      <div v-if="displayedAnswer" class="w-full mt-6">
        <div
          class="p-6 rounded-3xl bg-n-solid-2 outline outline-1 outline-n-weak"
        >
          <p class="text-3xl leading-relaxed text-n-slate-12">
            {{ displayedAnswer }}
          </p>
        </div>
        <div v-if="mode === 'classic'" class="flex justify-center mt-4">
          <button
            type="button"
            class="flex items-center gap-2 px-6 py-3 text-lg font-semibold text-white rounded-xl"
            :class="status === 'speaking' ? 'bg-n-ruby-9' : 'bg-n-teal-9'"
            @click="replayClassic"
          >
            <span
              :class="
                status === 'speaking' ? 'i-lucide-square' : 'i-lucide-volume-2'
              "
              class="size-6"
            />
            {{ status === 'speaking' ? 'Durdur' : 'Tekrar Dinle' }}
          </button>
        </div>
      </div>

      <!-- Yazarak sorma (yedek giriş yolu) -->
      <form class="flex w-full gap-2 mt-8" @submit.prevent="submitText">
        <input
          v-model="textInput"
          type="text"
          aria-label="Yazarak soru sor"
          placeholder="Ya da buraya yazarak sor…"
          class="flex-1 px-4 py-3 text-lg rounded-xl bg-n-solid-2 text-n-slate-12 outline outline-1 outline-n-weak"
        />
        <button
          type="submit"
          class="px-5 py-3 text-lg font-semibold text-white rounded-xl bg-n-brand disabled:opacity-50"
          :disabled="!textInput.trim()"
        >
          Sor
        </button>
      </form>

      <!-- Örnek sorular -->
      <div class="w-full mt-6">
        <p class="mb-2 text-base text-n-slate-11">Örnek sorular:</p>
        <div class="flex flex-wrap gap-2">
          <button
            v-for="ex in [
              'Günaydın, bugünkü brifingi ver',
              'Bugünü değerlendir, dünle kıyasla',
              'Şirket şu an kârda mı zararda mı?',
              'Satış botu dün nasıl çalıştı, cevapsız müşteri var mı?',
              'Kargolar ne durumda, teslim edilemeyen var mı?',
              'Hangi kanal daha iyi gidiyor?',
              'İşimin röntgenini çek, nereye odaklanmalıyım?',
            ]"
            :key="ex"
            type="button"
            class="px-4 py-2 text-base rounded-full bg-n-solid-2 text-n-slate-12 hover:bg-n-alpha-1"
            @click="askExample(ex)"
          >
            {{ ex }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>
